import React, { useState, useEffect } from 'react';
import { 
	Box, 
	Typography, 
	Button, 
	Paper, 
	TextField, 
	Alert,
	InputAdornment,
	IconButton,
	useTheme
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { loginUser, clearError } from '../../store/slices/authSlice';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';

const Login = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const muiTheme = useTheme();
	const { mode } = useAppTheme();
	const { loading, error, isAuthenticated } = useAppSelector((state) => state.auth);

	const [formData, setFormData] = useState({
		loginIDOrEmail: '',
		password: '',
	});
	const [showPassword, setShowPassword] = useState(false);
	const [formErrors, setFormErrors] = useState({
		loginIDOrEmail: '',
		password: '',
	});

	useEffect(() => {
		if (isAuthenticated) {
			const state = location.state as any;
			const fromPath = state?.from?.pathname ? `${state.from.pathname}${state.from.search || ''}` : '/dashboard';
			navigate(fromPath, { replace: true });
		}
	}, [isAuthenticated, navigate, location.state]);

	useEffect(() => {
		return () => {
			dispatch(clearError());
		};
	}, [dispatch]);

	const validateForm = () => {
		const errors = {
			loginIDOrEmail: '',
			password: '',
		};

		if (!formData.loginIDOrEmail.trim()) {
			errors.loginIDOrEmail = 'Login ID or Email is required';
		}

		if (!formData.password.trim()) {
			errors.password = 'Password is required';
		}
        // else if (formData.password.length < 6) {
		// 	errors.password = 'Password must be at least 6 characters';
		// }

		setFormErrors(errors);
		return !errors.loginIDOrEmail && !errors.password;
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
		
		if (formErrors[name as keyof typeof formErrors]) {
			setFormErrors(prev => ({
				...prev,
				[name]: ''
			}));
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		if (validateForm()) {
			// Determine if input is email or loginID
			const isEmail = /\S+@\S+\.\S+/.test(formData.loginIDOrEmail);
			const loginData = isEmail 
				? { email: formData.loginIDOrEmail, password: formData.password }
				: { loginID: formData.loginIDOrEmail, password: formData.password };
			
			await dispatch(loginUser(loginData));
		}
	};

	// Dynamic background gradient based on theme
	const getBackgroundGradient = () => {
		return mode === 'dark' 
			? 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)'
			: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
	};

	// Dynamic paper background based on theme
	const getPaperBackground = () => {
		return mode === 'dark' ? '#18191b' : '#ffffff';
	};

	// Dynamic border color based on theme
	const getBorderColor = () => {
		return mode === 'dark' ? '#333' : '#e0e0e0';
	};

	// Dynamic text color based on theme
	const getTextColor = () => {
		return mode === 'dark' ? 'grey.400' : 'grey.600';
	};

	// Dynamic input text color based on theme
	const getInputTextColor = () => {
		return mode === 'dark' ? 'white' : muiTheme.palette.text.primary;
	};

	return (
		<Box sx={{ 
			minHeight: '100vh', 
			display: 'flex', 
			alignItems: 'center', 
			justifyContent: 'center',
			p: 3,
			background: getBackgroundGradient()
		}}>
			<motion.div
				initial={{ opacity: 0, y: 40 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.7, ease: [0.4, 0.1, 0.2, 1] }}
			>
				<Paper
					sx={{
						p: 6,
						borderRadius: 4,
						bgcolor: getPaperBackground(),
						border: `1px solid ${getBorderColor()}`,
						textAlign: 'center',
						maxWidth: 450,
						width: '100%',
						boxShadow: mode === 'dark' 
							? '0 20px 40px rgba(0,0,0,0.3)' 
							: '0 20px 40px rgba(0,0,0,0.1)'
					}}
				>
					<Typography variant="h3" sx={{ 
						fontWeight: 700, 
						color: 'secondary.main', 
						mb: 2 
					}}>
						Welcome Back
					</Typography>
					
					<Typography variant="body1" sx={{ 
						color: getTextColor(), 
						mb: 4,
						lineHeight: 1.6
					}}>
						Sign in to your account to continue
					</Typography>

					{error && (
						<Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
							{error}
						</Alert>
					)}

					<Box component="form" onSubmit={handleSubmit} sx={{ textAlign: 'left' }}>
						<TextField
							fullWidth
							label="Login ID or Email"
							name="loginIDOrEmail"
							type="text"
							value={formData.loginIDOrEmail}
							onChange={handleInputChange}
							error={!!formErrors.loginIDOrEmail}
							helperText={formErrors.loginIDOrEmail}
							sx={{
								mb: 3,
								'& .MuiOutlinedInput-root': {
									borderRadius: 3,
									'& fieldset': {
										borderColor: getBorderColor(),
										borderWidth: '1px',
									},
									'&:hover fieldset': {
										borderColor: 'secondary.main',
									},
									'&.Mui-focused fieldset': {
										borderColor: 'secondary.main',
									},
								},
								'& .MuiInputLabel-root': {
									color: getTextColor(),
									'&.Mui-focused': {
										color: 'secondary.main',
									},
								},
								'& .MuiInputBase-input': {
									color: getInputTextColor(),
								},
							}}
						/>

						<TextField
							fullWidth
							label="Password"
							name="password"
							type={showPassword ? 'text' : 'password'}
							value={formData.password}
							onChange={handleInputChange}
							error={!!formErrors.password}
							helperText={formErrors.password}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											onClick={() => setShowPassword(!showPassword)}
											edge="end"
											sx={{ color: 'grey.400' }}
										>
											{showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								),
							}}
							sx={{
								mb: 4,
								'& .MuiOutlinedInput-root': {
									borderRadius: 3,
									'& fieldset': {
										borderColor: getBorderColor(),
										borderWidth: '1px',
									},
									'&:hover fieldset': {
										borderColor: 'secondary.main',
									},
									'&.Mui-focused fieldset': {
										borderColor: 'secondary.main',
									},
								},
								'& .MuiInputLabel-root': {
									color: getTextColor(),
									'&.Mui-focused': {
										color: 'secondary.main',
									},
								},
								'& .MuiInputBase-input': {
									color: getInputTextColor(),
								},
							}}
						/>

						<Button
							type="submit"
							fullWidth
							variant="contained"
							size="large"
							disabled={loading}
							sx={{
								bgcolor: 'secondary.main',
								color: 'white',
								py: 1.5,
								fontSize: 16,
								fontWeight: 600,
								mb: 3,
								borderRadius: 3,
								'&:hover': {
									bgcolor: 'secondary.dark',
								},
								'&:disabled': {
									bgcolor: 'grey.600',
									color: 'grey.400',
								},
							}}
						>
							{loading ? 'Signing In...' : 'Sign In'}
						</Button>
					</Box>

					<Box sx={{ mt: 4 }}>
						<Typography variant="body2" sx={{ color: getTextColor(), mb: 1 }}>
							Don't have an account?
						</Typography>
						<Button
							component={Link}
							to="/register"
							sx={{
								color: 'secondary.main',
								textDecoration: 'none',
								'&:hover': {
									color: 'secondary.dark',
								},
							}}
						>
							Create Account
						</Button>
					</Box>
				</Paper>
			</motion.div>
		</Box>
	);
};

export default Login; 